import React, { useEffect, useState } from "react";
import {
	TextField,
	Button,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import Logo from "../assets/parallex-logo.png";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const Login = () => {
	const [form, setForm] = useState({ email: "", password: "" });
	const [loading, setLoading] = useState(false);
	const [checkingAuth, setCheckingAuth] = useState(true);

	// Forgot Password State
	const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
	const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
	const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

	const navigate = useNavigate();

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!form.email || !form.password) {
			toast.error("Please fill in all fields");
			return;
		}

		setLoading(true);
		try {
			const res = await axios.post(
				"https://parallexsms.approot.ng/login.php",
				form,
				{ headers: { "Content-Type": "application/json" } }
			);

			if (res.data.success && res.data.requiresOTP) {
				toast.success(res.data.message || "OTP sent to your email!");
				navigate("/otp", {
					state: {
						userId: res.data.userId,
						email: form.email,
						password: form.password,
					},
				});
			} else if (!res.data.success) {
				toast.error(res.data.message || "Login failed");
			}
		} catch (err) {
			console.error(err);
			toast.error("An error occurred. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleForgotPassword = async () => {
		if (!forgotPasswordEmail) {
			toast.error("Please enter your email address");
			return;
		}

		setForgotPasswordLoading(true);
		try {
			const res = await axios.post(
				"https://parallexsms.approot.ng/forgot_password.php",
				{ email: forgotPasswordEmail },
				{ headers: { "Content-Type": "application/json" } }
			);

			if (res.data.success) {
				toast.success(res.data.message);
				setForgotPasswordOpen(false);
				setForgotPasswordEmail("");
			} else {
				toast.error(res.data.message);
			}
		} catch (err) {
			console.error(err);
			toast.error("An error occurred. Please try again.");
		} finally {
			setForgotPasswordLoading(false);
		}
	};


	useEffect(() => {
		const checkAuth = () => {
			const token = localStorage.getItem("parallextoken");
			if (token) {
				navigate("/dashboard");
			} else {
				setCheckingAuth(false);
			}
		};

		setTimeout(checkAuth, 1000);
	}, [navigate]);

	if (checkingAuth) {
		return (
			<div className='min-h-screen w-full flex flex-col justify-center items-center bg-[#f8f7fc]'>
				<img
  src={Logo}
  alt="Parallex Bank"
  className="mb-5 h-14 w-auto max-w-[220px] animate-pulse object-contain"
/>
				<CircularProgress color='warning' />
				<Typography variant='body2' className='mt-4 text-gray-600'>
					Checking your session...
				</Typography>
			</div>
		);
	}

	return (
		<>
			<div className='min-h-screen flex flex-col md:flex-row bg-[#f8f7fc]'>
				{/* Left section */}
				<div className='relative overflow-hidden bg-[#34206f] text-white flex flex-col justify-center items-center w-full md:w-1/2 px-6 sm:px-10 py-14 md:py-16 min-h-[280px] md:min-h-screen'>
					<img
  src={Logo}
  alt="Parallex Bank"
  className="mb-8 h-16 w-auto max-w-[240px] object-contain drop-shadow-sm"
/>
					<Typography variant='h4' className='font-bold mb-4 text-center'>
						Welcome to Parallex Bank
					</Typography>
					<Typography className='text-center text-sm sm:text-base leading-7 max-w-md text-white/85'>
						Securely manage SMS notifications, transactions, user activity and
						more.
					</Typography>
				</div>

				{/* Right section */}
				<div className='flex justify-center items-center w-full md:w-1/2 bg-[#f8f7fc] px-4 sm:px-8 py-10 md:py-12'>
					
						<form
							onSubmit={handleSubmit}
							className='bg-white px-6 sm:px-10 py-8 sm:py-10 rounded-3xl shadow-[0_20px_60px_rgba(43,25,90,0.10)] border border-[#eeeaf7] w-full max-w-md'
						>
							<div className='text-center mb-8'>
								<Typography variant='h5' className='text-primary font-bold'>
									Login to Continue
								</Typography>
							</div>

							<div className='space-y-5'>
								<TextField
									fullWidth
									label='Email Address'
									name='email'
									variant='outlined'
								sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff', '&.Mui-focused fieldset': { borderColor: '#DB9E27' } }, '& .MuiInputLabel-root.Mui-focused': { color: '#DB9E27' } }}
									value={form.email}
									onChange={handleChange}
								/>
								<TextField
									fullWidth
									label='Password'
									name='password'
									type='password'
									variant='outlined'
								sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff', '&.Mui-focused fieldset': { borderColor: '#DB9E27' } }, '& .MuiInputLabel-root.Mui-focused': { color: '#DB9E27' } }}
									value={form.password}
									onChange={handleChange}
								/>

								<div className='text-right'>
									<Button
										onClick={() => setForgotPasswordOpen(true)}
										sx={{
											color: "#DB9E27",
											textTransform: "none",
											fontSize: "0.875rem",
										}}
									>
										Forgot Password?
									</Button>
								</div>

								<Button
									type='submit'
									variant='contained'
									fullWidth
									disabled={loading}
									sx={{
										backgroundColor: "#DB9E27",
										color: "#fff",
										fontWeight: "bold",
										"&:hover": {
											backgroundColor: "#c5891e",
										},
									}}
								>
									{loading ? "Sending OTP..." : "Continue"}
								</Button>
							</div>

							<div className='text-center mt-7'>
								<Typography variant='body2' className='text-gray-500'>
									&copy; {new Date().getFullYear()} Parallex Bank. All rights reserved.
								</Typography>
							</div>
						</form>
					
				</div>
			</div>

			{/* Forgot Password Dialog */}
			<Dialog
				open={forgotPasswordOpen}
				onClose={() => setForgotPasswordOpen(false)}
				maxWidth='sm'
				fullWidth
			>
				<DialogTitle>
					<Typography variant='h6' fontWeight='bold' color='primary'>
						Reset Your Password
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Alert severity='info' sx={{ mb: 2 }}>
						Enter your email address and we'll send you instructions to reset
						your password.
					</Alert>
					<TextField
						autoFocus
						margin='dense'
						label='Email Address'
						type='email'
						fullWidth
						variant='outlined'
						value={forgotPasswordEmail}
						onChange={(e) => setForgotPasswordEmail(e.target.value)}
						disabled={forgotPasswordLoading}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button
						onClick={() => setForgotPasswordOpen(false)}
						disabled={forgotPasswordLoading}
					>
						Cancel
					</Button>
					<Button
						onClick={handleForgotPassword}
						variant='contained'
						disabled={forgotPasswordLoading}
						sx={{
							backgroundColor: "#DB9E27",
							"&:hover": {
								backgroundColor: "#c5891e",
							},
						}}
					>
						{forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default Login;
